import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import {
  clearAuthParams,
  clearRedirectPaths,
  hasRedirectPaths,
  isUserSelectCookieNewer,
  purgeUncollectableOidcState
} from '@/util/auth';
import { useEffect, useRef } from 'react';
import { AuthProvider, AuthProviderProps, hasAuthParams, useAuth } from 'react-oidc-context';

export const ViatelAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { config } = useConfig();

  const settings: AuthProviderProps = {
    authority: config.auth.url,
    client_secret: config.auth.client_secret,
    client_id: config.auth.client_id,
    redirect_uri: window.location.origin + '/openid/callback',
    response_type: config.auth.response_type,
    scope: config.auth.scope,
    revokeTokensOnSignout: true,
    post_logout_redirect_uri: window.location.origin
  };

  return (
    <AuthProvider {...settings}>
      <AuthManager>{children}</AuthManager>
    </AuthProvider>
  );
};

/**
 * This component is used to manage any global state changes for the authentication.
 */
const AuthManager = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();
  const { confirm } = useDialogs();
  const hasPurgedOidcState = useRef(false);
  const hasRedirectedForUserSelect = useRef(false);

  // Wait until react-oidc-context has finished processing any sign-in callback.
  // Effects run child-before-parent, so purging on mount would run before the
  // provider reads the state entry belonging to a sign-in that is in flight.
  useEffect(() => {
    if (auth.isLoading || hasPurgedOidcState.current) return;

    hasPurgedOidcState.current = true;
    purgeUncollectableOidcState();
  }, [auth.isLoading]);

  useEffect(() => {
    if (auth.isAuthenticated && hasAuthParams()) {
      if (hasRedirectPaths()) {
        clearRedirectPaths();
      }
      clearAuthParams();
    }
  }, [auth]);

  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      confirm({
        title: 'Session expiring',
        description: 'Your authenticated session is expiring or already expired. Press continue to stay signed in.',
        confirmationText: 'Continue'
      }).then((result) => {
        if (!result.confirmed) return;

        sessionStorage.setItem('authRedirectUrl', `${window.location.pathname}${window.location.search}`);
        if (auth.isAuthenticated) {
          auth.signinSilent();
        } else {
          auth.signinRedirect();
        }
      });
    });
  }, [auth.events, auth.signinSilent]);

  useEffect(() => {
    if (hasRedirectedForUserSelect.current || !isUserSelectCookieNewer(auth)) return;

    hasRedirectedForUserSelect.current = true;
    sessionStorage.setItem('authRedirectUrl', `${window.location.pathname}${window.location.search}`);
    auth.signinRedirect();
  }, [auth]);

  return <>{children}</>;
};
